import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { format } from 'date-fns';

const SEVERITY_COLORS = { low: 'var(--accent-primary)', medium: 'var(--accent-warning)', high: 'var(--accent-danger)', critical: '#ff0000' };

export default function ProctoringReview() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/attempts/${attemptId}/proctor/events`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load proctoring data'))
      .finally(() => setLoading(false));
  }, [attemptId]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!data) return null;

  const { events, report } = data;
  const trustColor = report?.trust_score >= 75 ? 'var(--accent-success)' : report?.trust_score >= 40 ? 'var(--accent-warning)' : 'var(--accent-danger)';

  const eventsByType = events.reduce((acc, ev) => {
    acc[ev.event_type] = (acc[ev.event_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header flex justify-between items-center">
          <div>
            <h1 className="page-title">Proctoring Review</h1>
            <p className="page-subtitle">Attempt ID: <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{attemptId.slice(0,8)}...</span></p>
          </div>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← Back</button>
        </div>

        {/* Summary cards */}
        <div className="card-grid card-grid-4 mb-6">
          <div className="stat-card">
            <div className="stat-value" style={{ color: trustColor, fontSize: 28 }}>{Math.round(report?.trust_score ?? 100)}%</div>
            <div className="stat-label">Trust Score</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--accent-warning)', fontSize: 28 }}>{report?.total_warnings || 0}</div>
            <div className="stat-label">Total Violations</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: 'var(--accent-danger)', fontSize: 28 }}>{report?.high_risk_events || 0}</div>
            <div className="stat-label">High Risk Events</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ fontSize: 22, color: report?.auto_terminated ? 'var(--accent-danger)' : 'var(--accent-success)' }}>
              {report?.auto_terminated ? 'YES' : 'NO'}
            </div>
            <div className="stat-label">Auto Terminated</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {/* Event type breakdown */}
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Violation Breakdown</h3>
            {Object.keys(eventsByType).length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--accent-success)', padding: 24 }}>✓ No violations recorded</div>
            ) : (
              Object.entries(eventsByType).sort((a,b) => b[1]-a[1]).map(([type, count]) => (
                <div key={type} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{type.replace(/_/g, ' ')}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-warning)' }}>{count}×</span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'var(--accent-warning)', borderRadius: 2, width: `${Math.min(100, (count / events.length) * 100)}%` }} />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Trust score visualizer */}
          <div className="card">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Trust Score Timeline</h3>
            <div style={{ position: 'relative', height: 160 }}>
              {events.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--accent-success)' }}>
                  Perfect session — no violations ✓
                </div>
              ) : (
                <svg width="100%" height="100%" viewBox="0 0 300 140">
                  {/* Grid lines */}
                  {[0, 25, 50, 75, 100].map(v => (
                    <g key={v}>
                      <line x1="30" y1={10 + (100-v) * 1.2} x2="290" y2={10 + (100-v) * 1.2} stroke="var(--border)" strokeWidth="0.5" />
                      <text x="25" y={14 + (100-v) * 1.2} fill="var(--text-muted)" fontSize="8" textAnchor="end">{v}</text>
                    </g>
                  ))}
                  {/* Trust score line */}
                  {(() => {
                    let score = 100;
                    const points = [{ x: 30, y: 10 }];
                    const severityWeights = { low: 2, medium: 5, high: 10, critical: 20 };
                    events.forEach((ev, i) => {
                      score = Math.max(0, score - (severityWeights[ev.severity] || 5));
                      const x = 30 + ((i + 1) / events.length) * 260;
                      points.push({ x, y: 10 + (100 - score) * 1.2 });
                    });
                    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
                    return (
                      <path d={pathD} fill="none" stroke="var(--accent-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    );
                  })()}
                </svg>
              )}
            </div>
          </div>
        </div>

        {/* Full event log */}
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Full Event Log ({events.length} events)</h3>
          {events.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--accent-success)', padding: 32 }}>
              ✓ Clean session — no violations detected
            </div>
          ) : (
            <div className="table-container" style={{ border: 'none' }}>
              <table>
                <thead>
                  <tr><th>Time</th><th>Event</th><th>Severity</th><th>Description</th><th>Confidence</th></tr>
                </thead>
                <tbody>
                  {events.map(ev => (
                    <tr key={ev.id}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {format(new Date(ev.timestamp), 'HH:mm:ss')}
                      </td>
                      <td style={{ fontWeight: 500, fontSize: 13 }}>{ev.event_type.replace(/_/g, ' ')}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: SEVERITY_COLORS[ev.severity], display: 'inline-block' }} />
                          {ev.severity}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{ev.description}
                        {ev.snapshot_url && ev.snapshot_url.startsWith('data:') && (
                          <div style={{ marginTop:6 }}>
                            <img
                              src={ev.snapshot_url}
                              alt="Violation snapshot"
                              style={{ maxWidth:160, borderRadius:6, border:'2px solid var(--accent-danger)', cursor:'pointer', display:'block' }}
                              onClick={() => window.open(ev.snapshot_url, '_blank')}
                              title="Click to view full screenshot"
                            />
                            <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>📷 Snapshot at time of violation</div>
                          </div>
                        )}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {ev.confidence_score ? `${(ev.confidence_score * 100).toFixed(0)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* AI Verdict */}
        <div className="card" style={{ marginTop: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>AI Integrity Assessment</h3>
          <div style={{ padding: 20, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
            {report?.trust_score >= 75 ? (
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--accent-success)', marginBottom: 8 }}>✓ Session appears clean</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Trust score of {Math.round(report?.trust_score)}% indicates the student maintained exam integrity throughout the session. No major violations were detected.</div>
              </div>
            ) : report?.trust_score >= 40 ? (
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--accent-warning)', marginBottom: 8 }}>⚠ Suspicious activity detected</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Trust score of {Math.round(report?.trust_score)}% indicates some suspicious behavior. Manual review recommended. {report?.high_risk_events} high-risk event(s) logged.</div>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--accent-danger)', marginBottom: 8 }}>🚨 High-risk session — likely integrity violation</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Trust score of {Math.round(report?.trust_score)}% is critically low. Multiple violations detected. Strong evidence of exam misconduct. Immediate review required.</div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
